<template>
  <UContainer
    :ui="{
      base: 'mx-auto relative',
      padding: 'px-4 sm:px-6 lg:px-8',
      constrained: 'max-w-[1920px]',
    }"
  >
    <div class="menu-page">
      <template v-if="category">
        <SeoBreadcrumbs :items="breadcrumbs" />

        <ProductCatalogSection
          :id="slug"
          :title="categoryName"
          :slug="slug"
          title-tag="h1"
          :items="category.products"
          eager-media
        />

        <!-- Остальные разделы меню. Это и навигация для гостя, который пришёл
             из поиска сразу на бургеры, и связность каталога для робота: без
             такого блока каждая страница раздела была бы тупиком. -->
        <nav v-if="otherCategories.length > 0" class="menu-page__siblings" aria-label="Другие разделы меню">
          <h2 class="menu-page__siblings-title">Другие разделы меню</h2>
          <ul class="menu-page__siblings-list">
            <li v-for="item in otherCategories" :key="item.to">
              <NuxtLink :to="categoryPath(item.to)" class="menu-page__sibling">{{ item.category }}</NuxtLink>
            </li>
          </ul>
        </nav>
      </template>

      <!-- Раздел выключили или переименовали, пока гость шёл по ссылке -->
      <div v-else-if="isReady" class="menu-page__missing">
        <h1 class="text-2xl md:text-3xl font-bold brand-font">Раздел меню не найден</h1>
        <p class="text-gray-600 dark:text-gray-400">
          Возможно, его убрали или переименовали. Всё меню — на главной.
        </p>
        <UiButtonMain color="red" size="xl" label="Перейти в меню" @click="navigateTo('/')" />
      </div>
    </div>
  </UContainer>
</template>

<script setup lang="ts">
/**
 * Страница раздела меню: /menu/<slug>.
 *
 * Каталог целиком жил на одном адресе, и запросу «бургеры в Новотитаровской»
 * поисковику было нечего показать: якорь #burgers — это та же главная с тем же
 * заголовком и тем же описанием. Здесь у каждого раздела свои title, description
 * и h1, а меню собирается из уже загруженного стора — отдельного запроса к API
 * не нужно, данные приезжают SSR-плагином institution-ssr.
 */
import { computed } from 'vue';
import { useInstitutionStore, useBreadcrumbsJsonLd } from '@entities/institution';
import { categoryPath } from '@shared/lib';
import { SeoBreadcrumbs } from '~/widgets/seo-breadcrumbs';

const props = defineProps<{ slug: string }>();

const storeInstitution = useInstitutionStore();

const slug = computed(() => props.slug);

/** Пока заведение не загрузилось, «не найдено» показывать рано — мигало бы на каждом заходе. */
const isReady = computed(() => Boolean(storeInstitution.institution));

const category = computed(() => {
  const found = (storeInstitution.products || []).find((item: any) => item?.to === slug.value);
  // Пустой раздел для гостя ничем не отличается от несуществующего, а в индексе
  // это страница без единого товара — держать её там незачем.
  return found?.products?.length > 0 ? found : null;
});

const categoryName = computed(() => category.value?.category || '');

const otherCategories = computed(() =>
  (storeInstitution.products || []).filter(
    (item: any) => item?.to && item.to !== slug.value && item?.products?.length > 0,
  ),
);

const institutionName = computed(() => storeInstitution.institution?.name?.trim() || '');
const city = computed(() => (storeInstitution.selectedBranch as any)?.address?.city?.trim() || '');

/**
 * Заголовок вкладки: «Бургеры — заказать с доставкой | Шаурмен, ст. Новотитаровская».
 * Город в title, потому что спрос у доставки еды местный: без него страница
 * конкурирует со всей страной, с ним — с соседней улицей.
 */
const pageTitle = computed(() => {
  if (!categoryName.value) return 'Раздел меню не найден';
  const where = [institutionName.value, city.value].filter(Boolean).join(', ');
  return where
    ? `${categoryName.value} — заказать с доставкой | ${where}`
    : `${categoryName.value} — заказать с доставкой`;
});

/**
 * Описание собирается из состава раздела: перечисление первых блюд и цена «от».
 *
 * Отдельного поля под описание раздела в админке нет, и заводить его на каждую
 * категорию каждого бренда — работа, которую никто не сделает. Состав меню
 * описывает раздел точнее любого шаблона и обновляется сам.
 */
const DESCRIPTION_LIMIT = 160;

const metaDescription = computed(() => {
  if (!category.value) return '';
  const items = (category.value.products || []) as any[];
  const names = items
    .map(item => (item?.name || item?.external_name || '').trim())
    .filter(Boolean)
    .slice(0, 5);

  const prices = items.map(item => Number(item?.price)).filter(value => Number.isFinite(value) && value > 0);
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;

  const where = city.value ? ` в ${city.value}` : '';
  const brand = institutionName.value ? ` — ${institutionName.value}` : '';
  const head = `${categoryName.value} с доставкой${where}${brand}.`;
  const list = names.length > 0 ? ` ${names.join(', ')}.` : '';
  const from = minPrice > 0 ? ` Цены от ${minPrice} ₽.` : '';

  const full = `${head}${list}${from}`.replaceAll(/\s+/g, ' ').trim();
  if (full.length <= DESCRIPTION_LIMIT) return full;
  const cut = full.slice(0, DESCRIPTION_LIMIT);
  return `${cut.slice(0, cut.lastIndexOf(' ')) || cut}…`;
});

const breadcrumbs = computed(() =>
  category.value
    ? [
        { name: 'Главная', path: '/' },
        { name: categoryName.value, path: categoryPath(slug.value) },
      ]
    : [],
);

useBreadcrumbsJsonLd(() => breadcrumbs.value);

/**
 * Несуществующий раздел обязан отдавать 404, а не 200 с текстом «не найдено»:
 * иначе в индексе копятся адреса-пустышки с одинаковым содержимым (soft 404).
 */
if (import.meta.server) {
  const event = useRequestEvent();
  if (event && isReady.value && !category.value) setResponseStatus(event, 404);
}

useSeoMeta({
  title: () => pageTitle.value,
  description: () => metaDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => metaDescription.value,
});

useHead(() => ({
  meta: category.value ? [] : [{ name: 'robots', content: 'noindex, follow' }],
}));
</script>

<style scoped lang="scss">
.menu-page {
  &__missing {
    @apply max-w-3xl mx-auto py-16 flex flex-col items-start gap-4;
  }

  &__siblings {
    @apply pt-16 md:pt-24;
  }

  &__siblings-title {
    @apply text-lg md:text-xl font-semibold text-gray-700 dark:text-gray-200;
    font-family: var(--app-brand-font, inherit);
  }

  &__siblings-list {
    @apply mt-4 flex flex-wrap gap-2;
  }

  &__sibling {
    @apply inline-flex px-4 py-2 text-sm font-semibold no-underline;
    @apply text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-zinc-800;
    @apply transition-colors hover:bg-gray-200 dark:hover:bg-zinc-700;
    border-radius: var(--app-radius-control, 9999px);
  }
}
</style>
